<script lang="ts" setup>
import dayjs from 'dayjs';
import { defaultLocations } from '~/constants/defaultLocations';
import { DELIVERY_SERVICES } from '~/constants/deliveryServices';
import type { OfferBalance } from '~/types/Catalog';
import type { DeliveryServiceDetailed } from '~/types/Checkout';

const catalogStore = useCatalogStore();

const isShowShopsModal = shallowRef<boolean>(false);
const isShowDeliveryTermsModal = shallowRef<boolean>(false);

// число всех офлайн магазинов с остатком > 0
const offlineAvailableShopsCount = computed(
  () =>
    catalogStore.selectedOffer.offer_balances.filter(
      (balance: OfferBalance) => balance.balance > 0 && balance.warehouse.id !== 2 && balance.warehouse.show,
    ).length,
);

// предыдущее число + слово "магазин" в правильном падеже и числе
const offlineShopsCountPluralized = computed(
  () =>
    `${offlineAvailableShopsCount.value} ${getNoun(offlineAvailableShopsCount.value ?? 0, 'магазина', 'магазинов', 'магазинов')}`,
);

// число магазинов, в которые можно привезти заказ для самовывоза
const shopsAvailableToPickup = computed(
  () =>
    catalogStore.selectedOffer.offer_balances.filter(
      (balance: OfferBalance) =>
        balance.warehouse.id !== 2 && !balance.warehouse.pickup_store?.only_stock && balance.warehouse.show,
    ).length,
);

// предыдущее число + слово "магазин" в правильном падеже и числе
const shopsAvailableToPickupPluralized = computed(
  () =>
    `${shopsAvailableToPickup.value} ${getNoun(shopsAvailableToPickup.value ?? 0, 'магазина', 'магазинов', 'магазинов')}`,
);

// показывать кнопку "самовывоз из Х магазинов"
const isShowSelfPickupBtn = computed(
  () => offlineAvailableShopsCount.value > 0 || (shopsAvailableToPickup.value > 0 && isAvailableInOnlineStore.value),
);

// булево, показывающее, есть ли остаток > 0 в интернет-магазине
const isAvailableInOnlineStore = computed(() => {
  const onlineStore = catalogStore.selectedOffer.offer_balances.find(
    (balance: OfferBalance) => balance.warehouse.id === 2 && balance.warehouse.show,
  );

  return onlineStore ? onlineStore.balance > 0 : false;
});

// число магазинов, из которых можно забрать товар для доставки
const shopsAvailableToTakeItemForDelivery = computed(
  () =>
    catalogStore.selectedOffer.offer_balances.filter(
      (balance: OfferBalance) =>
        balance.balance > 0 &&
        balance.warehouse.delivery_available &&
        balance.warehouse.pickup_store &&
        balance.warehouse.show,
    ).length,
);

// показывать кнопку "Узнать стоимость доставки"
const isShowDeliveryBtn = computed(
  () => isAvailableInOnlineStore.value || shopsAvailableToTakeItemForDelivery.value > 0,
);

const checkoutStore = useCheckoutStore();

const deliveries = ref<{ time: string; title: string; price: number | null }[]>([]);
const isLoading = ref(false);
const isDeliveryPricesLoaded = ref(false);
const availableDeliveriesDetails = ref<DeliveryServiceDetailed[]>([]);

function getFastestDelivery(deliveriesArray: DeliveryServiceDetailed[]) {
  return deliveriesArray.length
    ? deliveriesArray
        .filter((delivery) => delivery.delivery_period_min)
        .sort((a, b) => Number(a.delivery_period_min) - Number(b.delivery_period_min))[0]
    : null;
}

function calculateDeliveryTime(delivery: DeliveryServiceDetailed): string {
  let result: string;
  let closestDeliveryDay: dayjs.Dayjs;
  const today: dayjs.Dayjs = dayjs();

  if (delivery.slug === DELIVERY_SERVICES.russian_post.slug) {
    const fastestDelivery = getFastestDelivery(availableDeliveriesDetails.value);

    if (delivery.delivery_period_min) {
      closestDeliveryDay = dayjs().add(delivery.delivery_period_min, 'day'); // если [вдруг] у почты пришел свой расчет
    } else if (fastestDelivery?.delivery_period_min) {
      closestDeliveryDay = dayjs().add(fastestDelivery.delivery_period_min + 3, 'day'); // если у почты пусто, берем расчет самой быстрой доставки + 3 дня
    } else {
      closestDeliveryDay = dayjs().add(5, 'day'); // если ничего нет, то просто прибавляем 5 дней к сегодняшнему
    }
  } else {
    if (delivery.delivery_period_min) {
      closestDeliveryDay = dayjs().add(delivery.delivery_period_min + 1, 'day'); // для других доставок тоже добавляем 1 день к расчетному
    } else {
      closestDeliveryDay = dayjs().add(2, 'day');
    }
  }

  if (today.hour() > 19) closestDeliveryDay = closestDeliveryDay.add(1, 'day');

  if (closestDeliveryDay.date() === today.date()) {
    result = ' сегодня';
  } else if (closestDeliveryDay.date() === today.add(1, 'day').date()) {
    result = ' завтра';
  } else if (closestDeliveryDay.date() === today.add(2, 'day').date()) {
    result = ' послезавтра';
  } else {
    result = ` c&nbsp;${closestDeliveryDay.format(`D MMMM`)}`;
  }

  return result;
}

async function getAvailableDeliveries() {
  if (!checkoutStore.selectedLocation || isLoading.value) return;

  isDeliveryPricesLoaded.value = false;
  isLoading.value = true;
  deliveries.value = [];

  availableDeliveriesDetails.value = await checkoutStore.getDeliveryDetailsByOfferId(catalogStore.selectedOffer.id);

  const yandexDetails = availableDeliveriesDetails.value.find(
    (d) => d?.slug === DELIVERY_SERVICES['express-mkad'].slug,
  );

  // если выбрана Москва
  if (yandexDetails && checkoutStore.selectedLocation.data.kladr_id === defaultLocations[0].data.kladr_id) {
    deliveries.value.push({
      title: `Экспресс доставка ${yandexDetails?.title}`,
      time: getDeliveryDates(dayjs().get('hour') < 19 ? -1 : 0), // обратный костыль из-за прибавления 1 дня внутри функции расчета срока
      price: yandexDetails?.delivery_price ?? 1000,
    });
  }

  const otherDeliveries = availableDeliveriesDetails.value.filter(
    (d) => d?.slug !== DELIVERY_SERVICES['express-mkad'].slug && d?.slug !== DELIVERY_SERVICES.russian_post.slug,
  );

  if (otherDeliveries?.length) {
    const price = Math.min(...otherDeliveries.map((delivery) => delivery.delivery_price ?? Infinity));
    const title = otherDeliveries.map((delivery) => delivery.title).join(', ') + ', курьер';

    const fastestOtherDelivery = getFastestDelivery(otherDeliveries);

    deliveries.value.push({
      price: price === Infinity ? 0 : price,
      time: fastestOtherDelivery ? calculateDeliveryTime(fastestOtherDelivery) : '',
      title,
    });
  }

  const russianPostDetails = availableDeliveriesDetails.value.find(
    (d) => d?.slug === DELIVERY_SERVICES.russian_post.slug,
  );

  if (russianPostDetails) {
    deliveries.value.push({
      title: russianPostDetails?.title,
      time: calculateDeliveryTime(russianPostDetails),
      price: russianPostDetails?.delivery_price ?? 0,
    });
  }

  isDeliveryPricesLoaded.value = true;
  isLoading.value = false;
}

const cheapestDeliveryTerms = computed(() => {
  const cheapestDelivery = availableDeliveriesDetails.value.reduce(
    (cheapest: DeliveryServiceDetailed, delivery: DeliveryServiceDetailed) => {
      if (
        !cheapest ||
        delivery.delivery_price < cheapest.delivery_price ||
        (delivery.delivery_price === cheapest.delivery_price &&
          delivery.delivery_period_min &&
          (cheapest.delivery_period_min === null || delivery.delivery_period_min < cheapest.delivery_period_min))
      ) {
        return delivery;
      }
      return cheapest;
    },
    null as never as DeliveryServiceDetailed,
  );

  return cheapestDelivery
    ? `${calculateDeliveryTime(cheapestDelivery)}${
        cheapestDelivery.delivery_price === 0 ? ', бесплатно' : ` от&nbsp;${cheapestDelivery.delivery_price}&nbsp;₽`
      }`
    : null;
});

watch(
  () => checkoutStore.selectedLocation,
  () => getAvailableDeliveries(),
);

watch(
  () => isShowDeliveryTermsModal.value,
  () => {
    if (isShowDeliveryTermsModal.value && !deliveries.value.length) getAvailableDeliveries();
  },
);

onMounted(async () => {
  await checkoutStore.getUserLocation();
  await getAvailableDeliveries();
});
</script>

<template>
  <section
    v-if="isShowSelfPickupBtn || isShowDeliveryBtn"
    class="product-page__availability-block"
  >
    <button
      v-if="isShowSelfPickupBtn"
      class="product-page__availability-block-btn"
      name="shops-available-to-pickup"
      @click="isShowShopsModal = true"
    >
      <SvgoPin
        :font-controlled="false"
        class="size-5"
        filled
      />

      <span
        v-if="offlineAvailableShopsCount > 0"
        class="text-left"
      >
        Самовывоз из
        <span class="product-page__availability-block-btn--highlight"> {{ offlineShopsCountPluralized }} </span>,
        сегодня
      </span>

      <span
        v-else-if="shopsAvailableToPickup > 0"
        class="text-left"
      >
        Под заказ из
        <span class="product-page__availability-block-btn--highlight">{{ shopsAvailableToPickupPluralized }} </span>,
        завтра
      </span>
    </button>

    <button
      v-if="isShowDeliveryBtn"
      class="product-page__availability-block-btn"
      name="delivery-terms"
      @click="isShowDeliveryTermsModal = true"
    >
      <SvgoShipment
        :font-controlled="false"
        class="size-5"
        filled
      />
      <span
        v-if="checkoutStore.selectedLocation && cheapestDeliveryTerms && isDeliveryPricesLoaded"
        class="text-left leading-normal"
      >
        В
        <span class="product-page__availability-block-btn--highlight">
          {{
            checkoutStore.selectedLocation.data.city_with_type ??
            checkoutStore.selectedLocation.data.settlement_with_type
          }}
        </span>

        <span v-html="cheapestDeliveryTerms" />
      </span>

      <span
        v-else
        class="text-left"
      >
        Узнать условия
        <span class="product-page__availability-block-btn--highlight"> доставки </span>

        <Transition
          v-if="isLoading"
          name="loader"
        >
          <SvgoSpinner
            :font-controlled="false"
            class="animate-spin inline-block ml-1 size-3"
            title="Идет загрузка..."
          />
        </Transition>
      </span>
    </button>

    <ProductPageAvailabilityInShopsModal v-model="isShowShopsModal" />

    <ProductPageAvailableDeliveryTermsModal
      v-model="isShowDeliveryTermsModal"
      :deliveries
      :isLoading
    />
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__availability-block {
    display: flex;
    flex-direction: column;
  }

  &__availability-block-btn {
    display: flex;
    gap: 8px;
    justify-content: flex-start;
    align-items: center;
    overflow: hidden;
    padding: 12px;
    border-top: 1px solid $hell-black-10;
    border-right: 1px solid $hell-black-10;
    border-left: 1px solid $hell-black-10;
    background-color: $white;
    font-weight: $bold;
    font-size: $text-sm;

    &:first-of-type {
      border-top: 1px solid $hell-black-10;
      border-bottom: none;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    &:last-of-type {
      border-bottom: 1px solid $hell-black-10;
      border-bottom-right-radius: 12px;
      border-bottom-left-radius: 12px;
    }

    &--highlight {
      color: $hell-blue;
      transition: all 0.3s;
    }

    &:hover {
      .product-page__availability-block-btn--highlight {
        color: $label-violet;
      }
    }
  }
}
</style>
